<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationEvent, type NotificationPayload } from '@nsftx/seven-client-retail-vue-sgp'
import { logService } from '@nsftx/seven-client-retail-utils'
import { getWallets } from '@/modules/wallet/walletService'
import errorParser from '@/utils/errorParser'
import { useWalletStore } from '@/stores/wallet/walletStore'
import STWalletBalanceDetails from './STWalletBalanceDetails.vue'
import { useWalletBalanceStore } from '@/stores/wallet/walletBalanceStore'
import { useMoneyProcessingStore } from '@/stores/money-processing/moneyProcessingStore'
import { formatNumber } from '@nsftx/seven-nxg-retail-vue-sdk'

// stores
const { hasMultipleWallets, activeWallet } = storeToRefs(useWalletStore())
const { isMoneyTopUpIndicatorVisible, processingAmount, processingCurrency } =
  storeToRefs(useMoneyProcessingStore())
const walletBalanceStore = useWalletBalanceStore()
const { showWalletBalanceDetailsModal } = walletBalanceStore
const { isWalletBalanceVisible } = storeToRefs(walletBalanceStore)

const LOG_PREFIX = '[STWalletBalance]'

useNotificationEvent('ng::balance-update', (event: NotificationPayload<'ng::balance-update'>) => {
  logService.info(`${LOG_PREFIX} Balance update event received`, {
    details: {
      betslipStatus: event.content.betslipStatus
    },
    code: 'ST_WALLET_BALANCE_UPDATE_RECEIVED'
  })

  getWallets('ui').catch((err) => {
    logService.error(
      `${LOG_PREFIX} Failed to get wallets for the punter on balance update event.`,
      {
        code: 'ST_GET_WALLETS_FAILED',
        ...errorParser.parseUpstream(err)
      }
    )
  })
})

// computeds
// choosing primary wallet is not yet supported, so always display the first one in the list
const formattedWalletBalance = computed(() => {
  if (!isWalletBalanceVisible.value) {
    return '* * * *'
  }

  return formatNumber(activeWallet.value?.balance || '0', { style: 'decimal' })
})

const formattedProcessingAmount = computed(() => {
  if (!processingAmount.value) {
    return ''
  }

  return formatNumber(processingAmount.value, { style: 'decimal' })
})

// methods
const handleWalletBalanceClick = () => {
  showWalletBalanceDetailsModal()
}
</script>

<template>
  <div v-if="activeWallet" class="st-wallet-balance">
    <div
      class="st-wallet-balance__item st-pointer"
      data-testid="openWalletBalance"
      @click="handleWalletBalanceClick"
    >
      <div class="st-wallet-balance__count">
        <span v-if="hasMultipleWallets" class="st-wallet-balance__icon">
          <i class="n-i n-i-bills" />
        </span>

        <span class="st-wallet-balance__amount" data-testid="formattedWalletBalance">{{
          formattedWalletBalance
        }}</span>
      </div>
      <div class="st-wallet-balance__currency">
        <span>{{ activeWallet.currency }}</span>

        <i class="n-i n-i-arrow-down-d" />
      </div>
      <STWalletBalanceDetails />
    </div>
    <div
      v-if="isMoneyTopUpIndicatorVisible"
      class="st-wallet-balance__item"
      data-testid="moneyTopUpIndicator"
    >
      <div class="st-wallet-balance__count">
        <span class="st-wallet-balance__icon st-wallet-balance__icon--spinning">
          <i class="n-i n-i-refresh-ng" />
        </span>
        <span class="st-wallet-balance__amount">{{ formattedProcessingAmount }}</span>
      </div>
      <div class="st-wallet-balance__currency">
        <span>{{ processingCurrency }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.st-wallet-balance {
  display: flex;
  gap: var(--nxgds-alias-spacing-xs);

  &__item {
    display: flex;
    height: 56px;
    padding: var(--nxgds-alias-spacing-none) var(--nxgds-alias-spacing-sm);
    align-items: center;
    gap: var(--nxgds-alias-spacing-2xs);
    border-radius: var(--nxgds-alias-radius-corner-theme);
    background: var(--nxgds-mapped-color-surface-black-10);
  }

  &__count {
    display: flex;
    align-items: center;
    gap: var(--nxgds-alias-spacing-sm);
  }

  &__amount {
    color: var(--nxgds-mapped-color-text-on-brand-primary);
    text-align: right;
    font-family: var(--nxgds-alias-type-terminal-label-big-strong-font-family);
    font-size: var(--nxgds-alias-type-terminal-label-big-strong-size);
    font-weight: var(--nxgds-alias-type-terminal-label-big-strong-weight);
    line-height: var(--nxgds-alias-type-terminal-label-big-strong-line-height);
    letter-spacing: var(--nxgds-alias-type-terminal-label-big-strong-letter-spacing);
  }

  &__icon {
    color: var(--nxgds-mapped-color-icon-on-brand-primary);
    text-align: center;
    font-family: var(--nxgds-alias-type-terminal-icon-20-font-family);
    font-size: var(--nxgds-alias-type-terminal-icon-20-size);
    font-weight: 400;
    line-height: var(--nxgds-alias-type-terminal-icon-20-line-height);

    &--spinning {
      display: inline-flex;
      animation: st-wallet-balance-icon-spin 1.25s linear infinite;
    }
  }

  &__currency {
    color: var(--nxgds-mapped-color-text-on-brand-secondary);
    text-align: right;
    font-family: var(--nxgds-alias-type-terminal-label-big-regular-font-family);
    font-size: var(--nxgds-alias-type-terminal-label-big-regular-size);
    font-style: normal;
    font-weight: var(--nxgds-alias-type-terminal-label-big-regular-weight);
    line-height: var(--nxgds-alias-type-terminal-label-big-regular-line-height);
    letter-spacing: var(--nxgds-alias-type-terminal-label-big-regular-letter-spacing);

    i {
      color: var(--nxgds-mapped-color-icon-on-brand-primary);
      text-align: center;
      font-family: var(--nxgds-alias-type-terminal-icon-14-font-family);
      font-size: var(--nxgds-alias-type-terminal-icon-14-size);
      font-weight: 400;
      line-height: var(--nxgds-alias-type-terminal-icon-14-line-height);
      padding-left: var(--nxgds-alias-spacing-2xs);
    }
  }
}

@keyframes st-wallet-balance-icon-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
